import { useCallback } from "react";
import { toast } from "sonner";
import { type OfferPhase } from "@/lib/offer-calc";
import {
  calcReorderSortOrder,
  nextSortOrderFor,
  calcDuplicateAfterSortOrder,
  calcDuplicateAtTargetSortOrder,
  duplicateOfferLineFields,
  templateLineFields,
} from "@/lib/offer-edit-calc";
import {
  useCreateOfferLine,
  useUpdateOfferLine,
  useDeleteOfferLine,
  type OfferLine,
  type OfferLineTemplate,
} from "@/hooks/use-offers";

// Re-eksport så testene og andre forbrukere fortsatt kan importere fra hook-stien
export { calcReorderSortOrder, nextSortOrderFor } from "@/lib/offer-edit-calc";

interface UseOfferLineActionsArgs {
  offerId: string;
  lines: OfferLine[];
  hourlyRate: number;
  /** Hvilken seksjon nye linjer fra katalog skal opprettes i. null = hovedbudsjett. */
  catalogTargetSectionId: string | null;
  /** Sett auto-fokus-id på sist-opprettet linje (mengde-celle). */
  onLineCreated?: (lineId: string) => void;
  /**
   * Når false er tilbudet read-only (ikke-forfatter/ikke-admin). Alle
   * linje-handlere no-op-er, så ingen create/update/delete/reorder
   * persisteres. Default true. VIB-offer-author-gating.
   */
  canEdit?: boolean;
}

interface UseOfferLineActionsResult {
  handlePickTemplate: (template: OfferLineTemplate, qty: number | null) => Promise<void>;
  handleUpdateLine: (lineId: string, patch: Partial<OfferLine>) => void;
  handleDeleteLine: (lineId: string) => void;
  handleReorder: (sourceId: string, targetId: string) => void;
  handleMoveLine: (lineId: string, dir: "up" | "down") => void;
  handleDuplicateLine: (sourceId: string, targetId?: string | null) => Promise<void> | void;
  /** Beregner neste sort_order for fase+section. Eksportert primært for tester. */
  nextSortOrder: (phase: OfferPhase, sectionId: string | null) => number;
}

/**
 * Konsoliderer line-handlers (template-pick, update, delete, reorder, move)
 * fra OfferEditPage (VIB-658). Wrapper mutations fra use-offers og holder
 * sort-order-kalkulasjon på ett sted så det blir testbart.
 */
export function useOfferLineActions({
  offerId,
  lines,
  hourlyRate,
  catalogTargetSectionId,
  onLineCreated,
  canEdit = true,
}: UseOfferLineActionsArgs): UseOfferLineActionsResult {
  const createLine = useCreateOfferLine();
  const updateLine = useUpdateOfferLine(offerId);
  const deleteLine = useDeleteOfferLine(offerId);

  const nextSortOrder = useCallback(
    (phase: OfferPhase, sectionId: string | null) =>
      nextSortOrderFor(lines, phase, sectionId),
    [lines],
  );

  const handlePickTemplate = useCallback(
    async (template: OfferLineTemplate, qty: number | null) => {
      if (!canEdit) return;
      const fields = templateLineFields(template, qty, hourlyRate ?? 2000);
      const targetSectionId = catalogTargetSectionId;
      const created = await createLine.mutateAsync({
        offer_id: offerId,
        ...fields,
        sort_order: nextSortOrderFor(lines, template.phase, targetSectionId),
        section_id: targetSectionId,
      });
      onLineCreated?.(created.id);
    },
    [canEdit, offerId, lines, hourlyRate, catalogTargetSectionId, createLine, onLineCreated],
  );

  const handleUpdateLine = useCallback(
    (lineId: string, patch: Partial<OfferLine>) => {
      if (!canEdit) return;
      updateLine.mutate({ id: lineId, patch });
    },
    [canEdit, updateLine],
  );

  const handleDeleteLine = useCallback(
    (lineId: string) => {
      if (!canEdit) return;
      // Fang hele linja før sletting så "Angre" kan gjenopprette den intakt.
      const deleted = lines.find((l) => l.id === lineId);
      deleteLine.mutate(lineId, {
        onSuccess: () => {
          if (!deleted) return;
          toast("Linje slettet", {
            action: {
              label: "Angre",
              onClick: () => {
                createLine.mutate({
                  offer_id: deleted.offer_id,
                  template_id: deleted.template_id,
                  phase: deleted.phase,
                  description: deleted.description,
                  cost_type: deleted.cost_type,
                  pricing_type: deleted.pricing_type,
                  quantity: deleted.quantity,
                  unit: deleted.unit,
                  unit_price: deleted.unit_price,
                  purchase_price: deleted.purchase_price,
                  amount: deleted.amount,
                  comment: deleted.comment,
                  sort_order: deleted.sort_order,
                  section_id: deleted.section_id ?? null,
                  overtime_split: deleted.overtime_split ?? null,
                  auto_kind: deleted.auto_kind ?? null,
                  is_auto_generated: deleted.is_auto_generated ?? false,
                  source_idea_card_id: deleted.source_idea_card_id ?? null,
                });
              },
            },
          });
        },
      });
    },
    [canEdit, lines, deleteLine, createLine],
  );

  const handleReorder = useCallback(
    (sourceId: string, targetId: string) => {
      if (!canEdit) return;
      const result = calcReorderSortOrder(lines, sourceId, targetId);
      if (!result) return;
      const source = lines.find((l) => l.id === sourceId);
      if (!source) return;
      const patch: Partial<OfferLine> = { sort_order: result.sortOrder };
      if (result.phase !== source.phase) patch.phase = result.phase;
      if ((source.section_id ?? null) !== result.sectionId) {
        patch.section_id = result.sectionId;
      }
      updateLine.mutate({ id: sourceId, patch });
    },
    [canEdit, lines, updateLine],
  );

  const handleMoveLine = useCallback(
    (lineId: string, dir: "up" | "down") => {
      if (!canEdit) return;
      const line = lines.find((l) => l.id === lineId);
      if (!line) return;
      // Finn alle linjer i samme phase + section, sortert. Mobile flytt-knapper
      // bytter sort_order med naboline innen samme fase (matching av
      // OfferLineCard sin isFirst/isLast-grense).
      const peers = lines
        .filter(
          (l) =>
            l.phase === line.phase &&
            (l.section_id ?? null) === (line.section_id ?? null),
        )
        .sort((a, b) => a.sort_order - b.sort_order);
      const idx = peers.findIndex((l) => l.id === lineId);
      const neighborIdx = dir === "up" ? idx - 1 : idx + 1;
      if (neighborIdx < 0 || neighborIdx >= peers.length) return;
      const neighbor = peers[neighborIdx];
      // Bytt sort_order. To mutate-kall (kunne vært batchet, men enkelt for nå).
      updateLine.mutate({ id: line.id, patch: { sort_order: neighbor.sort_order } });
      updateLine.mutate({ id: neighbor.id, patch: { sort_order: line.sort_order } });
    },
    [canEdit, lines, updateLine],
  );

  const handleDuplicateLine = useCallback(
    async (sourceId: string, targetId?: string | null) => {
      if (!canEdit) return;
      const source = lines.find((l) => l.id === sourceId);
      if (!source || source.is_auto_generated) return;
      const placement =
        targetId && targetId !== sourceId
          ? calcDuplicateAtTargetSortOrder(lines, targetId)
          : calcDuplicateAfterSortOrder(lines, sourceId);
      if (!placement) return;
      try {
        const created = await createLine.mutateAsync(
          duplicateOfferLineFields(source, placement),
        );
        onLineCreated?.(created.id);
      } catch (err: unknown) {
        toast.error(`Kunne ikke duplisere linje: ${(err as Error).message}`);
      }
    },
    [canEdit, lines, createLine, onLineCreated],
  );

  return {
    handlePickTemplate,
    handleUpdateLine,
    handleDeleteLine,
    handleReorder,
    handleMoveLine,
    handleDuplicateLine,
    nextSortOrder,
  };
}
